import React, { useState, useCallback, cloneElement } from "react";
import {
  TextInput,
  SimpleForm,
  required,
  maxLength,
  number,
  regex,
  EditProps,
  SelectInput,
  EditContextProvider,
  Title,
  useEditController,
} from "react-admin";
import { Grid, Typography } from "@material-ui/core";
import { LatLngInput } from "./SchoolCreate";
import CustomImageInput from "components/CustomImageInput";
import SanitizedGrid from "components/SanitizedGrid";
import TitleBreadcrumb from "components/TitleBreadcrumb";
import CustomEditToolbar from "components/CustomEditToolbar";
import { emailValidation, mobilePhoneValidation } from "utils/validation";
import CustomOneMap from "./components/CustomOneMap";
import CustomAddressAutoCompleteInput from "./components/CustomAddressAutoCompleteInput";
import { areaCalculationType, pricingPolicyList } from "./const";
import { labelStyle } from "utils/style";

const EditForm = (props: any) => {
  const { aside } = props;
  const controllerProps = useEditController(props);
  const {
    basePath, // deduced from the location, useful for action buttons
    loaded, // boolean that is false until the record is available
    record, // record fetched via dataProvider.getOne() based on the id from the location
    redirect, // the default redirection route. Defaults to 'list'
    resource, // the resource name, deduced from the location. e.g. 'posts'
    save, // the update callback, to be passed to the underlying form as submit handler
    saving, // boolean that becomes true when the dataProvider is called to update the record
    version, // integer used by the refresh feature
    defaultTitle,
  } = controllerProps;

  const formatRecord = (res: any) => {
    if (res) {
      if (res.isFullCalendarDaysCharge) {
        res.pricingPolicy = "fullCalendarDays";
      } else {
        res.pricingPolicy = "contactDays";
      }

      return res;
    }
  };

  return (
    <EditContextProvider value={controllerProps}>
      <div>
        <Typography variant="h4" style={labelStyle}>
          {record?.name}
        </Typography>

        <Title title={defaultTitle} />

        {loaded &&
          cloneElement(props.children, {
            className: "MuiPaper-root MuiPaper-elevation1",
            basePath,
            record: formatRecord(record),
            redirect,
            resource,
            save,
            saving,
            version,
          })}
        {loaded &&
          aside &&
          cloneElement(aside, {
            basePath,
            record,
            resource,
            version,
            save,
            saving,
          })}
      </div>
    </EditContextProvider>
  );
};

const SchoolEdit = (props: EditProps) => {
  const [lat, setlat] = useState<string | undefined>("");
  const [lng, setLng] = useState<string | undefined>("");

  const initializeLocation = useCallback(
    ({ type, coordinate }: any) => {
      type === "lat" && setlat(Number(coordinate).toPrecision(8));
      type === "lng" && setLng(Number(coordinate).toPrecision(8));
    },
    [setlat, setLng]
  );

  const { id } = props;

  const breadcrumb = [
    { link: "/schools", title: "Schools" },
    { link: `/schools/${id}`, title: id },
  ];

  const transform = async (data: any) => {
    if (data.photoUrl) {
      if (data.photoUrl.url) {
        data.photoUrl = (data.photoUrl.url as string).split(",")[1];

        delete data.photoUrlFile;
      }
    } else {
      data.photoUrl = null;
    }

    if (data.pricingPolicy === "contactDays") {
      data.isFullCalendarDaysCharge = false;
    } else {
      data.isFullCalendarDaysCharge = true;
    }

    delete data.pricingPolicy;

    return data;
  };

  const SchoolPrefixFormater = (data: string) => {
    if (data) {
      return data.toUpperCase();
    }
  };

  const callback = (value: { lat: string; lng: string }) => {
    setlat(value.lat);
    setLng(value.lng);
  };

  return (
    <>
      <TitleBreadcrumb paths={breadcrumb} />
      <EditForm {...props} transform={transform} undoable={false}>
        <SimpleForm toolbar={<CustomEditToolbar />}>
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">School Information</Typography>
            </Grid>

            <Grid item xs={4}>
              <TextInput
                label="School Name"
                source="name"
                validate={required()}
              />
            </Grid>

            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <TextInput
                label="Email Address"
                source="email"
                type="email"
                validate={emailValidation}
              />
            </Grid>

            <Grid item xs={4}>
              <TextInput
                label="Contact Number"
                source="phone"
                // regular Expression for TextInput doesnt have white spaces
                validate={mobilePhoneValidation}
              />
            </Grid>

            <Grid item xs={4}></Grid>

            <Grid item xs={5}>
              <CustomImageInput
                label="Profile image"
                source="photoUrl"
                fieldType="image"
                accept="image/*"
                placeholder="Drop a images (*.jpg/jpeg/png) to upload, or click to select it."
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">School Address</Typography>
            </Grid>

            <Grid item xs={2}>
              <CustomAddressAutoCompleteInput
                label="Postal Code *"
                source="postalCode"
                referenceSource="locationData"
                parentCallback={callback}
              />
            </Grid>
            <Grid item xs={10}></Grid>

            <Grid item xs={2}>
              <TextInput
                label="Block No."
                source="blockNumber"
                validate={required()}
              />
            </Grid>
            <Grid item xs={10}></Grid>

            <Grid item xs={4}>
              <TextInput
                label="Address"
                source="address"
                validate={required()}
              />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={1}>
              <TextInput label="Unit No." source="unitNumber" />
            </Grid>
            <Grid item xs={3}>
              <TextInput label="Building Name" source="building" />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={12}>
              <Typography variant="h6">
                Map Location (Location for school's dropoff/pickup)
              </Typography>
            </Grid>

            <Grid item xs={6}>
              {lat !== "" && lng !== "" && (
                <CustomOneMap
                  center={{ lat, lng }}
                  referenceSource="locationData"
                />
              )}
              <LatLngInput
                lat={lat}
                lng={lng}
                updateLocation={initializeLocation}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Other Details</Typography>
            </Grid>

            <Grid item xs={3}>
              <TextInput
                label="School Prefix"
                source="schoolPrefix"
                format={SchoolPrefixFormater}
                validate={[required(), regex(/^[A-Za-z]+$/), maxLength(6)]}
                placeholder="Enter Up to 6 Alphabets"
              />
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={3}>
              <SelectInput
                source="areaDistanceCalculationType"
                label="Type for Student Area Distance Calculation"
                choices={areaCalculationType}
                validate={required()}
              />
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={3}>
              <SelectInput
                source="pricingPolicy"
                label="Pricing Policy"
                choices={pricingPolicyList}
                validate={required()}
              />
            </Grid>
            <Grid item xs={9}></Grid>
          </SanitizedGrid>
        </SimpleForm>
      </EditForm>
    </>
  );
};

export default SchoolEdit;
